/**
 * Workspace item taxonomies: Launch Types (marketing) + Investment Types (financial).
 *
 * Same shape as idea sources — catalog rows per workspace + slug stored on items.
 * Distinct from Workload Templates (GTM hours templates) and Funding envelopes.
 */

import { getSupabaseClient } from './supabaseClient';

export type ItemTaxonomyKind = 'launch_type' | 'investment_type';

export interface WorkspaceItemTaxonomy {
  id: string;
  workspaceId: string;
  value: string;
  label: string;
  description: string | null;
  isActive: boolean;
  isBuiltin: boolean;
  sortOrder: number;
  createdAt: string;
}

const TABLE: Record<ItemTaxonomyKind, string> = {
  launch_type: 'workspace_launch_types',
  investment_type: 'workspace_investment_types',
};

const SEED_RPC: Record<ItemTaxonomyKind, string> = {
  launch_type: 'seed_workspace_launch_types',
  investment_type: 'seed_workspace_investment_types',
};

const SELECT =
  'id, workspace_id, value, label, description, is_active, is_builtin, sort_order, created_at';

function rowToTaxonomy(row: Record<string, unknown>): WorkspaceItemTaxonomy {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    value: row.value as string,
    label: row.label as string,
    description: (row.description as string | null) ?? null,
    isActive: row.is_active as boolean,
    isBuiltin: row.is_builtin as boolean,
    sortOrder: row.sort_order as number,
    createdAt: row.created_at as string,
  };
}

function slugify(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 64) || 'custom';
}

export async function listItemTaxonomies(
  kind: ItemTaxonomyKind,
  workspaceId: string,
): Promise<{ data: WorkspaceItemTaxonomy[]; error: string | null }> {
  const supabase = getSupabaseClient();
  const table = TABLE[kind];
  const { data, error } = await supabase
    .from(table)
    .select(SELECT)
    .eq('workspace_id', workspaceId)
    .order('sort_order', { ascending: true });

  if (error) return { data: [], error: error.message };

  if (!data || data.length === 0) {
    const { error: seedError } = await supabase.rpc(SEED_RPC[kind], {
      p_workspace_id: workspaceId,
    });
    if (seedError) return { data: [], error: seedError.message };

    const { data: seeded, error: refetchError } = await supabase
      .from(table)
      .select(SELECT)
      .eq('workspace_id', workspaceId)
      .order('sort_order', { ascending: true });

    if (refetchError) return { data: [], error: refetchError.message };
    return { data: (seeded as Record<string, unknown>[]).map(rowToTaxonomy), error: null };
  }

  return { data: (data as Record<string, unknown>[]).map(rowToTaxonomy), error: null };
}

export async function listActiveItemTaxonomies(
  kind: ItemTaxonomyKind,
  workspaceId: string,
): Promise<{ data: WorkspaceItemTaxonomy[]; error: string | null }> {
  const { data, error } = await listItemTaxonomies(kind, workspaceId);
  return { data: data.filter((t) => t.isActive), error };
}

export async function setItemTaxonomyActive(
  kind: ItemTaxonomyKind,
  id: string,
  isActive: boolean,
): Promise<{ error: string | null }> {
  const { error } = await getSupabaseClient()
    .from(TABLE[kind])
    .update({ is_active: isActive })
    .eq('id', id);
  return { error: error?.message ?? null };
}

export async function updateItemTaxonomyLabel(
  kind: ItemTaxonomyKind,
  id: string,
  label: string,
  description?: string | null,
): Promise<{ error: string | null }> {
  const patch: Record<string, unknown> = { label };
  if (description !== undefined) patch.description = description;
  const { error } = await getSupabaseClient().from(TABLE[kind]).update(patch).eq('id', id);
  return { error: error?.message ?? null };
}

export async function createItemTaxonomy(
  kind: ItemTaxonomyKind,
  workspaceId: string,
  label: string,
  description: string | null,
  sortOrder: number,
): Promise<{ data: WorkspaceItemTaxonomy | null; error: string | null }> {
  const value = slugify(label);
  const { data, error } = await getSupabaseClient()
    .from(TABLE[kind])
    .insert({
      workspace_id: workspaceId,
      value,
      label,
      description,
      is_active: true,
      is_builtin: false,
      sort_order: sortOrder,
    })
    .select(SELECT)
    .single();
  if (error) return { data: null, error: error.message };
  return { data: rowToTaxonomy(data as Record<string, unknown>), error: null };
}

export async function deleteItemTaxonomy(
  kind: ItemTaxonomyKind,
  id: string,
): Promise<{ error: string | null }> {
  const { error } = await getSupabaseClient()
    .from(TABLE[kind])
    .delete()
    .eq('id', id)
    .eq('is_builtin', false);
  return { error: error?.message ?? null };
}

/** Fallback labels when catalog has not loaded (or value was retired). */
export const LAUNCH_TYPE_FALLBACK_LABELS: Record<string, string> = {
  product_launch: 'Product launch',
  feature_launch: 'Feature launch',
  pricing_packaging: 'Pricing & packaging',
  partner_gtm: 'Partner / co-sell',
  regional_expansion: 'Regional expansion',
  support_enablement: 'Support enablement',
  platform_capability: 'Platform capability',
  brand_campaign: 'Brand / demand campaign',
};

export const INVESTMENT_TYPE_FALLBACK_LABELS: Record<string, string> = {
  growth: 'Growth',
  maintain: 'Maintain',
  reduce_cost: 'Reduce cost',
  risk_compliance: 'Risk & compliance',
  platform_infra: 'Platform / infra',
  venture_option: 'Venture / option',
  customer_commitment: 'Customer commitment',
};
